import { jobSearchesService } from '@/services/job-searches.service'
import type { JobSearchCreateRequest } from '@/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const jobSearchesKeys = {
  all: ['job-searches'] as const,
  list: (skip = 0, limit = 20) => [...jobSearchesKeys.all, 'list', skip, limit] as const,
  detail: (searchId: number) => [...jobSearchesKeys.all, 'detail', searchId] as const,
}

export function useJobSearches(skip = 0, limit = 20) {
  return useQuery({
    queryKey: jobSearchesKeys.list(skip, limit),
    queryFn: () => jobSearchesService.list(skip, limit),
  })
}

export function useJobSearch(searchId: number | null, skip = 0, limit = 100) {
  return useQuery({
    queryKey: jobSearchesKeys.detail(searchId || 0),
    queryFn: () => jobSearchesService.get(searchId || 0, skip, limit),
    enabled: Boolean(searchId),
  })
}

export function useCreateJobSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: JobSearchCreateRequest) => jobSearchesService.create(payload),
    onSuccess: (search) => {
      queryClient.invalidateQueries({ queryKey: jobSearchesKeys.all })
      queryClient.setQueryData(jobSearchesKeys.detail(search.id), search)
    },
  })
}

export function useDeleteJobSearch() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (searchId: number) => jobSearchesService.delete(searchId),
    onSuccess: (_, searchId) => {
      queryClient.invalidateQueries({ queryKey: jobSearchesKeys.all })
      queryClient.removeQueries({ queryKey: jobSearchesKeys.detail(searchId) })
      queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
    },
  })
}
